import React from "react";
import { css, useTheme } from "@emotion/react";
import Image from "core/Components/Image";

import { blockEdgeGutter } from "elements/Components/BannerWithImage/components/blockLayout";

type Props = {
    image: Image,
    useFocusPoint: boolean,
    opacity: number,
    gradientColor: string,
    className?: string,
    isImageBlock?: boolean,
    isImageFirst?: boolean,
};

const ImageContainer: React.FC<Props> = ({
    image,
    useFocusPoint,
    opacity,
    gradientColor,
    className = '',
    isImageBlock = false,
    isImageFirst = false,
}) => {
    const theme = useTheme();

    const style = css`
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-self: center;

        ${theme.breakpoints.up("md")} {
            height: 500px;
        }

        ${theme.breakpoints.down("sm")} {
            height: 362px;
            min-width: 360px;
        }

        ${!useFocusPoint && css`
            background-image: url(${image.URL});
            background-size: cover;
            background-position: center;
        `};

        ${isImageBlock && css`
            ${theme.breakpoints.up("lg")} {
                display: none;
            }
        `};
    `;

    // Block mode (lg and up): the whole image is shown, height-locked to the
    // banner with width following the aspect ratio. Overlay/focus-point are
    // intentionally not applied here.
    const blockStyle = css`
        display: none;

        ${theme.breakpoints.up("lg")} {
            display: flex;
            flex: 1;
            min-width: 0;
            align-items: center;
            justify-content: center;
            height: 100%;

            ${blockEdgeGutter(theme, isImageFirst ? "left" : "right")}
        }
    `;

    // Fills the banner height, width follows the aspect ratio. When the column
    // is narrower than the image (the lg band) it simply overflows its column
    // and sits behind the text, rather than shrinking.
    const blockImageStyle = css`
        height: 100%;
        width: auto;
        display: block;
    `;

    const imageStyle = css`
        ${useFocusPoint && css`
            ${theme.breakpoints.down("md")} {
                object-fit: cover;
            }
        `}
        ${theme.breakpoints.up("5xl")} {
            height: 600px;
        }

        ${theme.breakpoints.only("md")} {
            height: 500px;
        }

        ${theme.breakpoints.down("sm")} {
            height: 362px;
        }
    `;

    return (
        <>
            <div css={style} className={className}>
                {useFocusPoint && (
                    <Image
                        image={image}
                        css={imageStyle}
                        useFocusPoint={true}
                        opacity={opacity}
                        gradientColor={gradientColor}
                    />
                )}
            </div>
            {isImageBlock && (
                <div css={blockStyle}>
                    <img
                        src={image.URL}
                        alt={image.AltText ?? ''}
                        css={blockImageStyle}
                        loading="lazy"
                    />
                </div>
            )}
        </>
    );
}

export default ImageContainer;
